import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

const GOLD = "#C9A84C";
const SERIF = "'Cormorant Garamond','Playfair Display',Georgia,serif";
const EASE = [0.16, 1, 0.3, 1];

function Particles({ count = 34, burst = false }) {
  const dots = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i, x: Math.random() * 100, size: 2 + Math.random() * 4, delay: Math.random() * 2.2, dur: 6 + Math.random() * 5,
    drift: (Math.random() - 0.5) * 12, angle: Math.random() * Math.PI * 2, dist: 25 + Math.random() * 35,
  })), [count]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {dots.map((d) => burst ? (
        <motion.span key={d.id} className="absolute rounded-full" style={{ left: "50%", top: "50%", width: d.size, height: d.size, background: GOLD, boxShadow: `0 0 ${d.size * 3}px ${GOLD}` }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
          animate={{ x: `${Math.cos(d.angle) * d.dist}vw`, y: `${Math.sin(d.angle) * d.dist - 10}vh`, opacity: [0, 1, 0.9, 0], scale: [0.4, 1, 1, 0.6] }}
          transition={{ duration: 3.6 + d.delay * 0.4, delay: 0.3 + d.delay * 0.25, ease: "easeOut" }} />
      ) : (
        <motion.span key={d.id} className="absolute rounded-full" style={{ left: `${d.x}%`, bottom: -10, width: d.size, height: d.size, background: GOLD, boxShadow: `0 0 ${d.size * 3}px ${GOLD}` }}
          initial={{ y: 0, opacity: 0, x: 0 }} animate={{ y: "-110vh", opacity: [0, 0.9, 0.9, 0], x: `${d.drift}vw` }}
          transition={{ duration: d.dur, delay: d.delay, ease: "linear", repeat: Infinity }} />
      ))}
    </div>
  );
}

function Seal() {
  return (
    <motion.div className="relative mx-auto mb-10" style={{ width: 150, height: 150 }}
      initial={{ scale: 0.6, opacity: 0, rotate: -40 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ duration: 2.2, ease: EASE }}>
      <motion.div className="absolute inset-0 rounded-full border" style={{ borderColor: GOLD }} animate={{ scale: [1, 1.08, 1], opacity: [0.9, 0.5, 0.9] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
      <div className="absolute inset-3 rounded-full border border-dashed" style={{ borderColor: `${GOLD}99` }} />
      <div className="absolute inset-0 grid place-items-center" style={{ fontFamily: SERIF, color: GOLD, fontSize: 64 }}>V</div>
    </motion.div>
  );
}

function VaultDoor() {
  return (
    <>
      <motion.div className="absolute inset-y-0 left-0 w-1/2" style={{ background: "linear-gradient(90deg,#141008,#0A0A0A)", borderRight: `1px solid ${GOLD}66` }}
        initial={{ x: 0 }} animate={{ x: "-100%" }} transition={{ duration: 3.2, delay: 0.6, ease: EASE }} />
      <motion.div className="absolute inset-y-0 right-0 w-1/2" style={{ background: "linear-gradient(270deg,#141008,#0A0A0A)", borderLeft: `1px solid ${GOLD}66` }}
        initial={{ x: 0 }} animate={{ x: "100%" }} transition={{ duration: 3.2, delay: 0.6, ease: EASE }} />
      <motion.div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2" style={{ width: 180, height: 180, borderColor: GOLD }}
        initial={{ rotate: 0, opacity: 1 }} animate={{ rotate: 540, opacity: 0, scale: 1.3 }} transition={{ duration: 2.4, ease: EASE }}>
        <div className="absolute inset-6 rounded-full border" style={{ borderColor: `${GOLD}aa` }} />
        <div className="absolute inset-0 grid place-items-center" style={{ color: GOLD, fontFamily: SERIF, fontSize: 48 }}>V</div>
      </motion.div>
    </>
  );
}

function Ripple() {
  return [0, 1, 2].map((i) => (
    <motion.div key={i} className="absolute left-1/2 top-1/2 rounded-full border" style={{ borderColor: GOLD, width: 80, height: 80, marginLeft: -40, marginTop: -40 }}
      initial={{ scale: 0.4, opacity: 0.9 }} animate={{ scale: 14, opacity: 0 }} transition={{ duration: 4.2, delay: i * 1.1, ease: "easeOut", repeat: Infinity }} />
  ));
}

function VaultKey() {
  return (
    <motion.div className="relative mx-auto mb-10" style={{ width: 160, height: 160 }} initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 2, ease: EASE }}>
      <motion.div className="absolute inset-0 rounded-full border-2" style={{ borderColor: GOLD }} animate={{ rotate: 360 }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }}>
        {[0, 90, 180, 270].map((a) => <span key={a} className="absolute left-1/2 top-0 h-3 w-[2px]" style={{ background: GOLD, transform: `rotate(${a}deg)`, transformOrigin: "50% 80px" }} />)}
      </motion.div>
      <motion.div className="absolute inset-5 rounded-full border" style={{ borderColor: `${GOLD}99` }} animate={{ rotate: -360 }} transition={{ duration: 26, repeat: Infinity, ease: "linear" }} />
      <div className="absolute inset-0 grid place-items-center" style={{ fontFamily: SERIF, color: GOLD, fontSize: 26, letterSpacing: "0.3em" }}>VCB</div>
    </motion.div>
  );
}

const pulseOpen = () => !!document.querySelector('[data-testid="security-pulse-modal"], [data-testid="pulse-modal"], [data-testid="security-pulse-gate"]');

export function Celebration({ stage, name = "", shareholdingPct, onDone, duration = 4200 }) {
  const [open, setOpen] = useState(true);
  const [held, setHeld] = useState(() => pulseOpen());
  useEffect(() => { if (!open) return undefined; const t = setInterval(() => setHeld(pulseOpen()), 400); return () => clearInterval(t); }, [open]);
  useEffect(() => { if (!stage || held || !open) return undefined; const t = setTimeout(() => setOpen(false), duration + 1600); return () => clearTimeout(t); }, [stage, duration, held, open]);
  if (!stage || held) return null;
  const line = (stage.line || "").replace("{name}", name.split(" ")[0] || name);
  const close = () => setOpen(false);
  return createPortal(
    <AnimatePresence onExitComplete={onDone}>
      {open && (
        <motion.div data-testid={`celebration-stage-${stage.n}`} onClick={close} role="dialog" aria-label={stage.title}
          className="fixed inset-0 z-[300] cursor-pointer select-none overflow-hidden" style={{ background: "#0A0A0A" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 1.2 } }} transition={{ duration: 1.4 }}>
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(201,168,76,0.10), transparent 60%)" }} />
          {stage.n === 4 && <motion.div className="absolute inset-0" style={{ background: `linear-gradient(100deg, transparent 30%, ${GOLD}22 50%, transparent 70%)` }} initial={{ x: "-100%" }} animate={{ x: "100%" }} transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }} />}
          {stage.n === 6 && <Ripple />}
          <Particles count={stage.n === 7 ? 60 : 32} burst={stage.n === 3} />
          {stage.n === 2 && <VaultDoor />}
          <div className="relative h-full w-full flex items-center justify-center px-6 text-center">
            <div className="max-w-3xl">
              {stage.n === 1 && <Seal />}
              {stage.n === 5 && <VaultKey />}
              {stage.n === 7 && (
                <motion.div className="mx-auto mb-10" style={{ fontFamily: SERIF, color: GOLD, fontSize: 120, lineHeight: 1 }}
                  initial={{ opacity: 0, y: 60, filter: "blur(14px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 2.6, ease: EASE }}>V</motion.div>
              )}
              <motion.div className="uppercase mb-6" style={{ color: GOLD, letterSpacing: "0.5em", fontSize: 11, fontFamily: "Manrope, sans-serif" }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: stage.n === 2 ? 2.6 : 0.8, duration: 1.4 }}>
                Stage {stage.n} · {stage.title}
              </motion.div>
              <motion.h1 style={{ fontFamily: SERIF, color: "#F3EEE2", fontSize: "clamp(28px, 5.2vw, 60px)", lineHeight: 1.18, letterSpacing: "0.04em", fontWeight: 400 }}
                initial={{ opacity: 0, y: 24, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ delay: stage.n === 2 ? 2.9 : 1.1, duration: 1.8, ease: EASE }}
                data-testid="celebration-line">
                {line}
              </motion.h1>
              {stage.n === 3 && (
                <motion.div className="mt-8" style={{ fontFamily: SERIF, color: GOLD, fontSize: "clamp(56px, 11vw, 128px)", lineHeight: 1, letterSpacing: "0.02em" }}
                  initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 2.2, duration: 1.6, ease: EASE }} data-testid="celebration-shareholding">
                  {Number(shareholdingPct || 0).toFixed(2)}%
                  <div className="uppercase" style={{ fontSize: 11, letterSpacing: "0.5em", color: `${GOLD}cc`, fontFamily: "Manrope, sans-serif", marginTop: 14 }}>Cap table · {name}</div>
                </motion.div>
              )}
              <motion.div className="mt-12 uppercase" style={{ color: `${GOLD}99`, letterSpacing: "0.4em", fontSize: 10, fontFamily: "Manrope, sans-serif" }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.4, duration: 1 }}>Click anywhere to continue</motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
